import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/i18n/I18nContext";

export const CandidatsSection = () => {
  const { t, lang } = useI18n();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({}); // translated notes by id

  useEffect(() => {
    api.get("/candidates/public")
      .then(({ data }) => setCandidates(data))
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
  }, []);

  // Translate candidate notes with AI when language is not French
  useEffect(() => {
    if (lang === "fr") { setNotes({}); return; }
    let cancelled = false;
    const withNotes = candidates.filter((c) => c.note && c.note.trim());
    Promise.all(
      withNotes.map((c) =>
        api.post("/translate", { text: c.note, target: lang })
          .then(({ data }) => [c.id, data.text])
          .catch(() => [c.id, c.note])
      )
    ).then((pairs) => { if (!cancelled) setNotes(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [lang, candidates]);

  const STATUS_META = {
    retenu: { label: t("candidats.retenu"), cls: "text-theatre-gold border-theatre-gold/40" },
    ecarte: { label: t("candidats.ecarte"), cls: "text-neutral-400 border-white/20" },
    refuse: { label: t("candidats.refuse"), cls: "text-theatre-red border-theatre-red/40" },
  };

  const groups = ["retenu", "ecarte", "refuse"].map((s) => ({
    status: s,
    items: candidates.filter((c) => c.status === s),
  }));

  return (
    <section
      id="candidats"
      data-testid="candidats-section"
      className="relative py-28 md:py-36 px-6 lg:px-10 border-t border-white/5"
      style={{ backgroundImage: `linear-gradient(rgba(5,5,5,0.92), rgba(5,5,5,0.97)), url(${IMAGES.architecture})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-theatre-gold mb-5">{t("candidats.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">{t("candidats.title")}</h2>
          <p className="mt-5 text-neutral-400 max-w-2xl mx-auto">{t("candidats.subtitle")}</p>
        </div>

        {loading ? (
          <p className="text-center text-neutral-500 font-mono text-sm" data-testid="candidats-loading">{t("candidats.loading")}</p>
        ) : candidates.length === 0 ? (
          <div className="text-center border border-theatre-gold/20 bg-black/50 py-16 px-6 max-w-2xl mx-auto" data-testid="candidats-empty">
            <p className="font-serif text-2xl text-white">{t("candidats.emptyTitle")}</p>
            <p className="mt-4 text-neutral-400">{t("candidats.emptyText")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {groups.map((g) => (
              <div key={g.status} data-testid={`candidats-group-${g.status}`}>
                <h3 className={`font-mono text-xs tracking-[0.2em] uppercase border-b pb-3 mb-5 ${STATUS_META[g.status].cls}`}>
                  {STATUS_META[g.status].label} ({g.items.length})
                </h3>
                <div className="space-y-3">
                  {g.items.map((c, i) => (
                    <motion.div
                      key={c.id}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.04 }}
                      data-testid={`candidat-card-${c.id}`}
                      className="bg-black/60 border border-white/10 p-5 hover:border-theatre-gold/30 transition-colors duration-300"
                    >
                      <p className="font-serif text-lg text-white">{c.prenom} {c.nom}</p>
                      <p className="text-xs text-neutral-500 mt-1">{c.parti || t("candidats.independant")} · {c.region}</p>
                      {c.note && <p className="text-sm text-neutral-400 mt-3 italic">{notes[c.id] || c.note}</p>}
                    </motion.div>
                  ))}
                  {g.items.length === 0 && (
                    <p className="text-sm text-neutral-600 italic">{t("candidats.aucun")}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
